import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Copyright 1999-2020. Plesk International GmbH. All rights reserved.
import React, { cloneElement, Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Measure from 'react-measure';
import { CLS_PREFIX } from '../../constants';

/**
 * `Grid` component is used to layout and align content based on 12-column system.
 * @since 0.0.48
 */
var Grid =
/*#__PURE__*/
function (_Component) {
  _inherits(Grid, _Component);

  function Grid() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, Grid);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(Grid)).call.apply(_getPrototypeOf2, [this].concat(args)));

    _defineProperty(_assertThisInitialized(_this), "state", {
      xs: _this.props.xs
    });

    _defineProperty(_assertThisInitialized(_this), "handleResize", function (_ref) {
      var width = _ref.bounds.width;
      var maxColumnsNumber = _this.props.maxColumnsNumber;

      if (typeof maxColumnsNumber === 'function') {
        maxColumnsNumber = maxColumnsNumber({
          width: width
        });
      }

      var xs = Math.max(Math.floor((width + _this.getGapSize()) / (_this.props.minColumnWidth + _this.getGapSize())), 1);

      _this.setState({
        xs: Math.min(xs, maxColumnsNumber)
      });

      if (_this.props.onColumnsChange) {
        _this.props.onColumnsChange(xs);
      }
    });

    return _this;
  }

  _createClass(Grid, [{
    key: "getGapSize",
    value: function getGapSize() {
      switch (this.props.gap) {
        case 'xs':
          return 8;

        case 'sm':
          return 12;

        case 'md':
          return 16;

        case 'lg':
          return 24;

        case 'xl':
          return 32;

        default:
          return 0;
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _classNames;

      var _this$props = this.props,
          baseClassName = _this$props.baseClassName,
          className = _this$props.className,
          minColumnWidth = _this$props.minColumnWidth,
          onColumnsChange = _this$props.onColumnsChange,
          maxColumnsNumber = _this$props.maxColumnsNumber,
          xs = _this$props.xs,
          sm = _this$props.sm,
          md = _this$props.md,
          lg = _this$props.lg,
          xl = _this$props.xl,
          gap = _this$props.gap,
          children = _this$props.children,
          props = _objectWithoutProperties(_this$props, ["baseClassName", "className", "minColumnWidth", "onColumnsChange", "maxColumnsNumber", "xs", "sm", "md", "lg", "xl", "gap", "children"]);

      var computedClassName = classNames(baseClassName, (_classNames = {}, _defineProperty(_classNames, "".concat(baseClassName, "--xs-").concat(this.state.xs), this.state.xs), _defineProperty(_classNames, "".concat(baseClassName, "--sm-").concat(sm), sm), _defineProperty(_classNames, "".concat(baseClassName, "--md-").concat(md), md), _defineProperty(_classNames, "".concat(baseClassName, "--lg-").concat(lg), lg), _defineProperty(_classNames, "".concat(baseClassName, "--xl-").concat(xl), xl), _defineProperty(_classNames, "".concat(baseClassName, "--gap-xs"), gap === 'xs'), _defineProperty(_classNames, "".concat(baseClassName, "--gap-sm"), gap === 'sm'), _defineProperty(_classNames, "".concat(baseClassName, "--gap-md"), gap === 'md'), _defineProperty(_classNames, "".concat(baseClassName, "--gap-lg"), gap === 'lg'), _defineProperty(_classNames, "".concat(baseClassName, "--gap-xl"), gap === 'xl'), _classNames), className);
      var result = React.createElement("div", _extends({
        className: computedClassName
      }, props), minColumnWidth > 0 && !this.state.xs ? null : children);

      if (minColumnWidth > 0) {
        return React.createElement(Measure, {
          bounds: true,
          onResize: this.handleResize
        }, function (_ref2) {
          var measureRef = _ref2.measureRef;
          return cloneElement(result, {
            ref: measureRef
          });
        });
      }

      return result;
    }
  }]);

  return Grid;
}(Component);

Grid.propTypes = {
  /**
   * Content of the `Grid`.
   * @since 0.0.48
   */
  children: PropTypes.node,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string,

  /**
   * Minimal column width
   * @since 0.0.50
   */
  minColumnWidth: PropTypes.number,

  /**
   * Maximum columns number
   * @since 0.0.53
   */
  maxColumnsNumber: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),

  /**
   * Number of columns for `xs` breakpoint
   * @since 0.0.53
   */
  xs: PropTypes.PropTypes.oneOf([1, 2, 3, 4, 5, 6, 'auto']),

  /**
   * Number of columns for `sm` breakpoint
   * @since 0.0.48
   */
  sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 'auto']),

  /**
   * Number of columns for `md` breakpoint
   * @since 0.0.48
   */
  md: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 'auto']),

  /**
   * Number of columns for `lg` breakpoint
   * @since 0.0.48
   */
  lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 'auto']),

  /**
   * Number of columns for `xl` breakpoint
   * @since 0.0.48
   */
  xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 'auto']),

  /**
   * Specifying the gutters between grid items
   * @since 0.0.53
   */
  gap: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),

  /**
   * Columns number change notifier
   * @since 0.0.50
   */
  onColumnsChange: PropTypes.func
};
Grid.defaultProps = {
  children: null,
  className: null,
  baseClassName: "".concat(CLS_PREFIX, "grid"),
  minColumnWidth: null,
  maxColumnsNumber: 6,
  xs: null,
  sm: null,
  md: null,
  lg: null,
  xl: null,
  gap: null,
  onColumnsChange: null
};
export default Grid;